import * as React from 'react';
import { Input, type InputProps } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { cn } from 'lib/utils';

/**
 * Shared input styling so every auth field reads as one system: 44px, white,
 * 10px radius, no leading icon. 16px text on phones keeps iOS from zooming.
 */
export const authInputClassName =
  'h-11 rounded-[10px] border-border bg-card px-3 text-base transition-colors md:text-[15px] ' +
  'placeholder:text-muted-foreground/70 hover:border-foreground/20 ' +
  'aria-[invalid=true]:border-destructive aria-[invalid=true]:hover:border-destructive';

export interface AuthFieldProps extends Omit<InputProps, 'icon' | 'label' | 'hint'> {
  label: string;
  error?: string;
  /** Marks the field wrong without a message of its own, e.g. both fields after bad credentials. */
  invalid?: boolean;
  /** Non-blocking advisory shown below the field (e.g. "Caps Lock is on"). */
  warning?: string;
  /** Control rendered at the trailing edge, e.g. a password reveal toggle. */
  rightSlot?: React.ReactNode;
  containerClassName?: string;
}

/** Forwards the ref + native props so it drops into react-hook-form's `register()`. */
export const AuthField = React.forwardRef<HTMLInputElement, AuthFieldProps>(
  ({ label, error, invalid, warning, rightSlot, id, name, className, containerClassName, ...props }, ref) => {
    const inputId = id ?? name;
    return (
      <div className={cn('space-y-1.5', containerClassName)}>
        <Label htmlFor={inputId} className="text-[13.5px] font-medium text-foreground/80">
          {label}
        </Label>
        <div className="relative">
          <Input
            ref={ref}
            id={inputId}
            name={name}
            aria-invalid={error || invalid ? true : undefined}
            className={cn(authInputClassName, rightSlot && 'pr-11', className)}
            {...props}
          />
          {rightSlot ? <div className="absolute right-1 top-1/2 -translate-y-1/2">{rightSlot}</div> : null}
        </div>
        {error ? (
          <p className="text-xs font-medium text-error-700 dark:text-error-400">{error}</p>
        ) : warning ? (
          <p className="text-xs font-medium text-warning-700 dark:text-warning-400">{warning}</p>
        ) : null}
      </div>
    );
  },
);

AuthField.displayName = 'AuthField';
